import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import toast from 'react-hot-toast'
import { marketingAPIService, MarketingContact } from '../services/api'
import {
  Plus,
  Download,
  Search,
  Users,
  Building2,
  Mail,
  Phone,
  Tag,
  X,
  Trash2,
  Ban,
  CheckCircle,
  AlertCircle
} from 'lucide-react'

const contactSchema = z.object({
  email: z.string().email('Invalid email address'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  phone: z.string().optional(),
  companyName: z.string().optional(),
  tags: z.string().optional(),
})

type ContactFormData = z.infer<typeof contactSchema>

const MarketingContacts: React.FC = () => {
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterUnsubscribed, setFilterUnsubscribed] = useState<boolean | undefined>(undefined)
  const [page, setPage] = useState(1)
  const [importing, setImporting] = useState(false)

  const { data: contactsData, isLoading } = useQuery(
    ['marketing-contacts', searchQuery, filterUnsubscribed, page],
    () =>
      marketingAPIService.getContacts({
        search: searchQuery || undefined,
        unsubscribed: filterUnsubscribed,
        page,
        limit: 20,
      })
  )

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  })

  const createMutation = useMutation(
    (data: any) => marketingAPIService.createContact(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('marketing-contacts')
        setShowCreateModal(false)
        reset()
        toast.success('Contact created successfully')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to create contact')
      },
    }
  )

  const unsubscribeMutation = useMutation(
    (contactId: string) => marketingAPIService.unsubscribeContact(contactId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('marketing-contacts')
        toast.success('Contact unsubscribed')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to unsubscribe')
      },
    }
  )

  const deleteMutation = useMutation(
    (contactId: string) => marketingAPIService.deleteContact(contactId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('marketing-contacts')
        toast.success('Contact deleted')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to delete contact')
      },
    }
  )

  const onSubmit = (data: ContactFormData) => {
    const tags = data.tags ? data.tags.split(',').map(t => t.trim()) : []
    createMutation.mutate({
      ...data,
      tags,
    })
  }

  const handleImportFromBusinesses = async () => {
    setImporting(true)
    try {
      const result = await marketingAPIService.importFromBusinesses()
      queryClient.invalidateQueries('marketing-contacts')
      toast.success(
        `Successfully imported ${result.imported} businesses! Skipped ${result.skipped}.`
      )
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to import contacts')
    } finally {
      setImporting(false)
    }
  }

  const handleUnsubscribe = (contactId: string, name: string) => {
    if (window.confirm(`Are you sure you want to unsubscribe ${name}?`)) {
      unsubscribeMutation.mutate(contactId)
    }
  }

  const handleDelete = (contactId: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete ${name}?`)) {
      deleteMutation.mutate(contactId)
    }
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Marketing Contacts</h1>
        <p className="text-gray-600">
          Manage contacts from businesses who signed up for ServiceFlow Pro
        </p>
      </div>

      {/* Stats */}
      {contactsData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Contacts</p>
                <p className="text-2xl font-bold text-gray-900">
                  {contactsData.pagination.total.toLocaleString()}
                </p>
              </div>
              <Users className="h-10 w-10 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Subscribers</p>
                <p className="text-2xl font-bold text-green-600">
                  {contactsData.contacts.filter((c) => !c.unsubscribed).length}
                </p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Unsubscribed</p>
                <p className="text-2xl font-bold text-gray-600">
                  {contactsData.contacts.filter((c) => c.unsubscribed).length}
                </p>
              </div>
              <Ban className="h-10 w-10 text-gray-400" />
            </div>
          </div>
        </div>
      )}

      {/* Actions Bar */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or company..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setPage(1)
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Filter */}
        <select
          value={filterUnsubscribed === undefined ? 'all' : filterUnsubscribed ? 'unsubscribed' : 'subscribed'}
          onChange={(e) => {
            const value = e.target.value
            setFilterUnsubscribed(
              value === 'all' ? undefined : value === 'unsubscribed'
            )
            setPage(1)
          }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Contacts</option>
          <option value="subscribed">Subscribed Only</option>
          <option value="unsubscribed">Unsubscribed Only</option>
        </select>

        {/* Import Button */}
        <button
          onClick={handleImportFromBusinesses}
          disabled={importing}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
        >
          <Download className="h-5 w-5" />
          {importing ? 'Importing...' : 'Import Businesses'}
        </button>

        {/* Create Button */}
        <button
          onClick={() => {
            setShowCreateModal(true)
            reset()
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Add Contact
        </button>
      </div>

      {/* Import Info Banner */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-blue-900 mb-1">
            Import ServiceFlow Business Signups
          </h3>
          <p className="text-sm text-blue-700">
            Click "Import Businesses" to automatically import all active businesses that have signed up for ServiceFlow Pro. 
            Each business owner will be added as a contact with their company information and subscription details.
          </p>
        </div>
      </div>

      {/* Contacts Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading contacts...</p>
          </div>
        ) : contactsData?.contacts?.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">
              No contacts yet. Import businesses to get started!
            </p>
            <button
              onClick={handleImportFromBusinesses}
              disabled={importing}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {importing ? 'Importing...' : 'Import Businesses Now'}
            </button>
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tags
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {contactsData?.contacts?.map((contact) => (
                  <tr key={contact.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {contact.firstName} {contact.lastName}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Mail className="h-4 w-4" />
                          {contact.email}
                        </div>
                        {contact.phone && (
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Phone className="h-4 w-4" />
                            {contact.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {contact.companyName ? (
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-900">
                            {contact.companyName}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {contact.subscriptionPlan ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                          {contact.subscriptionPlan}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {contact.tags.slice(0, 3).map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700"
                          >
                            <Tag className="h-3 w-3" />
                            {tag}
                          </span>
                        ))}
                        {contact.tags.length > 3 && (
                          <span className="px-2 py-1 text-xs text-gray-500">
                            +{contact.tags.length - 3} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          contact.unsubscribed
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {contact.unsubscribed ? 'Unsubscribed' : 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      {!contact.unsubscribed && (
                        <button
                          onClick={() =>
                            handleUnsubscribe(
                              contact.id,
                              `${contact.firstName} ${contact.lastName}`
                            )
                          }
                          className="text-yellow-600 hover:text-yellow-900"
                          title="Unsubscribe"
                        >
                          <Ban className="h-5 w-5" />
                        </button>
                      )}
                      <button
                        onClick={() =>
                          handleDelete(
                            contact.id,
                            `${contact.firstName} ${contact.lastName}`
                          )
                        }
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {contactsData && contactsData.pagination.totalPages > 1 && (
              <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing {((page - 1) * contactsData.pagination.limit) + 1} to{' '}
                  {Math.min(page * contactsData.pagination.limit, contactsData.pagination.total)} of{' '}
                  {contactsData.pagination.total} contacts
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= contactsData.pagination.totalPages}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Contact Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black opacity-30"
              onClick={() => setShowCreateModal(false)}
            />

            <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Add Contact</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      {...register('firstName')}
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      {...register('lastName')}
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.lastName.message}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    {...register('email')}
                    type="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    {...register('phone')}
                    type="tel"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name
                  </label>
                  <input
                    {...register('companyName')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tags (comma-separated)
                  </label>
                  <input
                    {...register('tags')}
                    type="text"
                    placeholder="vip, enterprise, trial"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createMutation.isLoading ? 'Creating...' : 'Create Contact'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MarketingContacts

